import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import { siteMeta } from "@/mocks/chigua";
import { getArticle, getChannel, channelArticles, kindBadge, formatHeat } from "@/lib/chiguaData";
import useSeo from "@/hooks/useSeo";
import Breadcrumbs from "@/components/chigua/Breadcrumbs";
import JsonLd from "@/components/chigua/JsonLd";
import StoryCard from "@/components/chigua/StoryCard";

export default function StoryDetail() {
  const { slug = "" } = useParams();
  const [copied, setCopied] = useState(false);
  const article = getArticle(slug);
  const channel = article ? getChannel(article.channel) : undefined;
  const related = article
    ? channelArticles(article.channel).filter((a) => a.slug !== article.slug).slice(0, 3)
    : [];
  const heat = article ? formatHeat(article.heat) : "";

  useSeo({
    title: article ? `${article.title}|今日吃瓜深扒` : "内容不存在 - 今日吃瓜",
    description: article ? article.excerpt : "您访问的内容不存在或已被下架，请返回首页浏览其他复盘。",
    path: `/story/${slug}`,
    keywords: `今日吃瓜,${article ? article.tags.join(",") : ""},内幕深扒,内幕真相`,
  });

  if (!article || !channel) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center md:px-6">
        <p className="text-7xl font-black text-background-300">404</p>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">该内容不存在或已被调整</h1>
        <Link to="/" className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50">
          返回首页
        </Link>
      </div>
    );
  }

  const copyLink = () => {
    const url = `${siteMeta.domain}/story/${article.slug}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      });
    }
  };

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date,
      author: { "@type": "Person", name: article.author },
      articleSection: channel.name,
      keywords: article.tags.join(","),
      inLanguage: "zh-CN",
      url: `${siteMeta.domain}/story/${article.slug}`,
      mainEntityOfPage: `${siteMeta.domain}/story/${article.slug}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${siteMeta.domain}/` },
        { "@type": "ListItem", position: 2, name: channel.name, item: `${siteMeta.domain}/list/${channel.slug}` },
        { "@type": "ListItem", position: 3, name: article.title, item: `${siteMeta.domain}/story/${article.slug}` },
      ],
    },
  ];

  return (
    <div className="mx-auto max-w-[1280px] px-4 md:px-6">
      <JsonLd data={schema} />
      <Breadcrumbs items={[{ label: channel.name, to: `/list/${channel.slug}` }, { label: article.title }]} />

      {/* 文章头 */}
      <header className="mx-auto max-w-[820px] text-center">
        <div className="flex items-center justify-center gap-2">
          <Link
            to={`/list/${channel.slug}`}
            className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold text-foreground-700 transition hover:bg-primary-100"
          >
            {channel.name}
          </Link>
          <span className={`rounded px-2 py-0.5 text-xs font-semibold text-background-50 ${kindBadge(article.kind)}`}>
            {article.kind}
          </span>
        </div>
        <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
          {article.title}
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-7 text-foreground-600 md:text-base">{article.dek}</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 border-y border-background-200 py-3 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="grid h-6 w-6 place-items-center rounded-full bg-primary-100 font-label-sc text-xs text-primary-700">
              {article.author.slice(0, 1)}
            </span>
            {article.author}
          </span>
          <time className="inline-flex items-center gap-1">
            <i className="ri-calendar-line" aria-hidden="true" />
            {article.date}
          </time>
          <span className="inline-flex items-center gap-1">
            <i className="ri-time-line" aria-hidden="true" />
            {article.minutes} 分钟阅读
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-fire-line text-accent-500" aria-hidden="true" />
            {heat} 热度
          </span>
        </div>
      </header>

      {/* 正文区 */}
      <div className="mx-auto mt-10 grid max-w-[1080px] gap-10 pb-16 lg:grid-cols-12">
        {/* 目录（桌面） */}
        <aside className="hidden lg:col-span-3 lg:block">
          <div className="sticky top-24 rounded-xl border border-background-200 bg-background-50 p-5">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-foreground-500">
              <i className="ri-list-unordered" aria-hidden="true" />
              本页目录
            </p>
            <ul className="mt-3 space-y-2 text-sm">
              {article.sections.map((s, i) => (
                <li key={i}>
                  <a
                    href={`#sec-${i}`}
                    className="flex items-start gap-2 rounded px-2 py-1.5 text-foreground-600 transition hover:bg-primary-100/60 hover:text-primary-700"
                  >
                    <span className="mt-0.5 text-[10px] text-foreground-400">{String(i + 1).padStart(2, "0")}</span>
                    {s.heading}
                  </a>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={copyLink}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-background-100 py-2.5 text-sm font-medium text-foreground-700 transition hover:bg-primary-100 hover:text-primary-700"
            >
              <i className={copied ? "ri-check-line text-green-600" : "ri-link"} aria-hidden="true" />
              {copied ? "链接已复制" : "复制链接"}
            </button>
          </div>
        </aside>

        {/* 文章主体 */}
        <article className="lg:col-span-6">
          <p className="border-l-4 border-primary-400 pl-4 text-[15px] leading-7 text-foreground-700 md:text-base">
            {article.excerpt}
          </p>

          {article.timeline && (
            <section className="mt-8 rounded-xl border border-accent-200/70 bg-accent-100/40 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="grid h-8 w-8 place-items-center rounded-md bg-accent-500 text-background-50">
                  <i className="ri-node-tree" aria-hidden="true" />
                </span>
                事件时间线
              </h2>
              <ol className="mt-5">
                {article.timeline.map((p) => (
                  <li key={p.time} className="relative flex gap-4 pb-6 last:pb-0">
                    <span className="flex flex-col items-center">
                      <span className="mt-0.5 h-3 w-3 rounded-full border-2 border-accent-500 bg-background-50" aria-hidden="true" />
                      <span className="mt-1 w-px flex-1 bg-accent-300/70" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <span className="rounded bg-accent-500/90 px-2 py-0.5 font-label-sc text-xs uppercase tracking-widest text-background-50">
                        {p.time}
                      </span>
                      <p className="mt-2 font-heading text-[15px] font-bold text-foreground-950">{p.title}</p>
                      <p className="mt-1 text-sm leading-6 text-foreground-600">{p.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {article.sections.map((s, i) => (
            <section key={i} id={`sec-${i}`} className="scroll-mt-24 pt-8">
              {s.heading && (
                <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950 md:text-2xl">{s.heading}</h2>
              )}
              {s.paragraphs.map((p, j) => (
                <p key={j} className="mb-4 text-[15px] leading-8 text-foreground-700 md:text-base">
                  {p}
                </p>
              ))}
            </section>
          ))}

          {/* 编者立场 */}
          <aside className="mt-8 rounded-xl border border-secondary-200 bg-secondary-100/50 p-5 text-sm leading-6 text-foreground-700">
            <p className="flex items-center gap-2 font-bold text-foreground-950">
              <i className="ri-scales-3-line text-secondary-700" aria-hidden="true" />
              编者立场
            </p>
            <p className="mt-2">
              本篇仅按公开材料整理内幕消息与内幕真相相关线索，重点在于时间线与信源层级的呈现，不做主观猜测与道德评判。涉及未定论事件之处，请以当事人与官方后续公告为准。
            </p>
          </aside>

          {/* 标签 */}
          <div className="mt-8 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-foreground-400">关联标签</span>
            {article.tags.map((t) => (
              <span key={t} className="rounded-full border border-background-200 bg-background-100 px-3 py-1 text-xs text-foreground-700">
                #{t}
              </span>
            ))}
          </div>
        </article>

        {/* 侧栏 相关 */}
        <aside className="space-y-6 lg:col-span-3">
          {related.length > 0 && (
            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-file-list-3-line text-primary-600" aria-hidden="true" />
                相关推荐
              </h3>
              <div className="mt-4 space-y-4">
                {related.map((a) => (
                  <StoryCard key={a.slug} item={a} />
                ))}
              </div>
              <Link
                to={`/list/${channel.slug}`}
                className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-700 transition hover:text-primary-800"
              >
                返回{channel.name}栏目
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
          )}

          <div className="rounded-xl bg-foreground-950 p-5 text-background-100">
            <p className="font-heading text-base font-bold text-background-50">本文由「今日吃瓜」整理</p>
            <p className="mt-2 text-sm leading-6 text-background-300">
              想看更多内幕深扒与内幕真相的时间线复盘？关注我们，不漏掉任何一条线索。
            </p>
            <Link
              to="/list/shenba"
              className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
            >
              前往深扒档案
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}